import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Skeleton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Sync as SyncIcon,
  BugReport as BugReportIcon,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  LinkOutlined as LinkIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { githubIssuesApi, teamApi } from '@/services/api';
import type { GitHubIssue, Repository } from '@/types';

const stateColors: Record<string, 'success' | 'error' | 'default'> = {
  open: 'success',
  closed: 'error',
};

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function GitHubIssuesPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Filters
  const [repoFilter, setRepoFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('open');
  const [jiraFilter, setJiraFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Selection for bulk JIRA creation
  const [selected, setSelected] = useState<string[]>([]);

  // Dialogs
  const [syncLogsOpen, setSyncLogsOpen] = useState(false);
  const [repoSettingsOpen, setRepoSettingsOpen] = useState(false);

  // Link JIRA dialog
  const [linkJiraIssueId, setLinkJiraIssueId] = useState<string | null>(null);
  const [linkJiraKey, setLinkJiraKey] = useState('');

  // Pending auto-JIRA changes (batch save)
  const [pendingAutoJira, setPendingAutoJira] = useState<Record<string, boolean>>({});
  const hasPendingAutoJira = Object.keys(pendingAutoJira).length > 0;

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Queries
  const { data: team } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamApi.get(teamId!).then((r) => r.data),
    enabled: !!teamId,
  });

  const { data: repos } = useQuery({
    queryKey: ['repositories', teamId],
    queryFn: () => teamApi.getRepositories(teamId!).then((r) => r.data),
    enabled: !!teamId,
  });

  const githubRepos = (() => {
    const all = repos?.filter((r: Repository) => r.platform === 'github' && r.is_active) || [];
    const seen = new Set<string>();
    return all.filter((r: Repository) => {
      if (seen.has(r.url)) return false;
      seen.add(r.url);
      return true;
    });
  })();

  const { data: issuesData, isLoading: issuesLoading } = useQuery({
    queryKey: ['github-issues', teamId, repoFilter, stateFilter, jiraFilter, page, pageSize],
    queryFn: () =>
      githubIssuesApi
        .list(teamId!, {
          repository_id: repoFilter || undefined,
          state: stateFilter || undefined,
          has_jira: jiraFilter === 'yes' ? true : jiraFilter === 'no' ? false : undefined,
          page: page + 1,
          page_size: pageSize,
        })
        .then((r) => r.data),
    enabled: !!teamId,
  });

  const { data: stats } = useQuery({
    queryKey: ['github-issues-stats', teamId],
    queryFn: () => githubIssuesApi.getStats(teamId!).then((r) => r.data),
    enabled: !!teamId,
  });

  const { data: syncLogs } = useQuery({
    queryKey: ['github-sync-logs', teamId],
    queryFn: () => githubIssuesApi.getSyncLogs(teamId!, 20).then((r) => r.data),
    enabled: !!teamId && syncLogsOpen,
  });

  // Mutations
  const syncAllMutation = useMutation({
    mutationFn: () => githubIssuesApi.syncAll(teamId!),
    onSuccess: (res) => {
      const results = res.data;
      const total = results.reduce((sum: number, r: { issues_fetched: number }) => sum + r.issues_fetched, 0);
      setSnackbar({ open: true, message: `Sync complete: ${total} issues fetched from ${results.length} repos`, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['github-issues'] });
      queryClient.invalidateQueries({ queryKey: ['github-issues-stats'] });
      queryClient.invalidateQueries({ queryKey: ['github-sync-logs'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Sync failed. Check logs for details.', severity: 'error' });
    },
  });

  const createJiraMutation = useMutation({
    mutationFn: (issueId: string) => githubIssuesApi.createJira(teamId!, issueId),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'JIRA ticket created successfully', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['github-issues'] });
      queryClient.invalidateQueries({ queryKey: ['github-issues-stats'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to create JIRA ticket', severity: 'error' });
    },
  });

  const createJiraBulkMutation = useMutation({
    mutationFn: (issueIds: string[]) => githubIssuesApi.createJiraBulk(teamId!, issueIds),
    onSuccess: (res) => {
      const data = res.data as { created: number; failed: number };
      setSnackbar({
        open: true,
        message: `Bulk JIRA creation: ${data.created} created, ${data.failed} failed`,
        severity: data.failed > 0 ? 'error' : 'success',
      });
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ['github-issues'] });
      queryClient.invalidateQueries({ queryKey: ['github-issues-stats'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Bulk JIRA creation failed', severity: 'error' });
    },
  });

  const updateAutoJiraBulkMutation = useMutation({
    mutationFn: (updates: Array<{ repository_id: string; auto_create_jira: boolean }>) =>
      githubIssuesApi.updateAutoJiraBulk(teamId!, updates),
    onSuccess: () => {
      setPendingAutoJira({});
      queryClient.invalidateQueries({ queryKey: ['repositories'] });
      setSnackbar({ open: true, message: 'Auto-create JIRA settings saved', severity: 'success' });
    },
    onError: () => {
      setPendingAutoJira({});
      setSnackbar({ open: true, message: 'Failed to save auto-create JIRA settings', severity: 'error' });
    },
  });

  const linkJiraMutation = useMutation({
    mutationFn: ({ issueId, jiraKey }: { issueId: string; jiraKey: string }) =>
      githubIssuesApi.linkJira(teamId!, issueId, jiraKey),
    onSuccess: () => {
      setLinkJiraIssueId(null);
      setLinkJiraKey('');
      setSnackbar({ open: true, message: 'JIRA ticket linked successfully', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['github-issues'] });
      queryClient.invalidateQueries({ queryKey: ['github-issues-stats'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to link JIRA ticket', severity: 'error' });
    },
  });

  const unlinkJiraMutation = useMutation({
    mutationFn: (issueId: string) => githubIssuesApi.unlinkJira(teamId!, issueId),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'JIRA ticket unlinked', severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['github-issues'] });
      queryClient.invalidateQueries({ queryKey: ['github-issues-stats'] });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to unlink JIRA ticket', severity: 'error' });
    },
  });

  // Handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const unlinked = (issuesData?.items || []).filter((i: GitHubIssue) => !i.jira_ticket_key).map((i: GitHubIssue) => i.id);
      setSelected(unlinked);
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (issueId: string, checked: boolean) => {
    setSelected((prev) => (checked ? [...prev, issueId] : prev.filter((id) => id !== issueId)));
  };

  const getEffectiveAutoJira = (repo: Repository) => {
    if (repo.id in pendingAutoJira) {
      return pendingAutoJira[repo.id];
    }
    return repo.auto_create_jira || false;
  };

  const handleAutoJiraToggle = (repo: Repository, newValue: boolean) => {
    const currentValue = repo.auto_create_jira || false;
    setPendingAutoJira((prev) => {
      const next = { ...prev };
      if (newValue === currentValue) {
        delete next[repo.id];
      } else {
        next[repo.id] = newValue;
      }
      return next;
    });
  };

  const handleSaveAutoJira = () => {
    const updates = Object.entries(pendingAutoJira).map(([repoId, value]) => ({
      repository_id: repoId,
      auto_create_jira: value,
    }));
    updateAutoJiraBulkMutation.mutate(updates);
  };

  const issues = issuesData?.items || [];
  const total = issuesData?.total || 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/teams/${teamId}`)}>
            {team?.name || 'Team'} /
          </Typography>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReportIcon /> GitHub Issues Tracker
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<SettingsIcon />} onClick={() => setRepoSettingsOpen(!repoSettingsOpen)}>
            Repo Settings
          </Button>
          <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => setSyncLogsOpen(true)}>
            Sync History
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={() => syncAllMutation.mutate()}
            disabled={syncAllMutation.isPending}
          >
            {syncAllMutation.isPending ? 'Syncing...' : 'Sync Now'}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Issues', value: stats?.total, color: '#1976d2' },
          { label: 'Open', value: stats?.open, color: '#2e7d32' },
          { label: 'Closed', value: stats?.closed, color: '#d32f2f' },
          { label: 'Linked to JIRA', value: stats?.with_jira, color: '#ed6c02' },
          { label: 'No JIRA', value: stats?.without_jira, color: '#9e9e9e' },
        ].map((stat) => (
          <Grid item xs={6} sm={4} md={2.4} key={stat.label}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h4" sx={{ color: stat.color, fontWeight: 600 }}>
                  {stat.value != null ? stat.value : <Skeleton width={40} sx={{ mx: 'auto' }} />}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Repository Settings (collapsible) */}
      <Collapse in={repoSettingsOpen}>
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Auto-create JIRA
              </Typography>
              {githubRepos.length > 0 && (
                <Button
                  size="small"
                  onClick={() => {
                    const allEnabled = githubRepos.every((r: Repository) => getEffectiveAutoJira(r));
                    githubRepos.forEach((r: Repository) => handleAutoJiraToggle(r, !allEnabled));
                  }}
                >
                  {githubRepos.every((r: Repository) => getEffectiveAutoJira(r)) ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </Box>
            {githubRepos.length === 0 ? (
              <Typography color="text.secondary">No active GitHub repositories found.</Typography>
            ) : (
              <>
                <Grid container spacing={0}>
                  {githubRepos.map((repo: Repository) => (
                    <Grid item xs={12} sm={6} key={repo.id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={getEffectiveAutoJira(repo)}
                            onChange={(e) => handleAutoJiraToggle(repo, e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {repo.name || repo.url}
                          </Typography>
                        }
                        sx={{ mr: 0 }}
                      />
                    </Grid>
                  ))}
                </Grid>
                {hasPendingAutoJira && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setPendingAutoJira({})}>
                      Discard
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSaveAutoJira}
                      disabled={updateAutoJiraBulkMutation.isPending}
                    >
                      {updateAutoJiraBulkMutation.isPending ? 'Saving...' : `Save ${Object.keys(pendingAutoJira).length} change(s)`}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Collapse>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Repository</InputLabel>
          <Select value={repoFilter} label="Repository" onChange={(e) => { setRepoFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All Repositories</MenuItem>
            {githubRepos.map((repo: Repository) => (
              <MenuItem key={repo.id} value={repo.id}>
                {repo.name || repo.url}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>State</InputLabel>
          <Select value={stateFilter} label="State" onChange={(e) => { setStateFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>JIRA Linked</InputLabel>
          <Select value={jiraFilter} label="JIRA Linked" onChange={(e) => { setJiraFilter(e.target.value); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="yes">Linked</MenuItem>
            <MenuItem value="no">Not Linked</MenuItem>
          </Select>
        </FormControl>
        {selected.length > 0 && (
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={() => createJiraBulkMutation.mutate(selected)}
            disabled={createJiraBulkMutation.isPending}
          >
            Create JIRA for {selected.length} issues
          </Button>
        )}
      </Box>

      {/* Issues Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < issues.filter((i: GitHubIssue) => !i.jira_ticket_key).length}
                  checked={issues.filter((i: GitHubIssue) => !i.jira_ticket_key).length > 0 && selected.length === issues.filter((i: GitHubIssue) => !i.jira_ticket_key).length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>#</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Repository</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Labels</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>JIRA</TableCell>
              <TableCell>JIRA Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {issuesLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 10 }).map((__, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : issues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>
                    No issues found. Click "Sync Now" to fetch issues from GitHub.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              issues.map((issue: GitHubIssue) => (
                <TableRow key={issue.id} hover>
                  <TableCell padding="checkbox">
                    {!issue.jira_ticket_key && (
                      <Checkbox
                        checked={selected.includes(issue.id)}
                        onChange={(e) => handleSelectOne(issue.id, e.target.checked)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Link href={issue.html_url} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      #{issue.github_issue_number}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Tooltip title={issue.title}>
                      <Typography variant="body2" noWrap>
                        {issue.title}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {issue.repository_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={issue.state} color={stateColors[issue.state] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>{issue.author || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(issue.labels || []).slice(0, 3).map((label: string) => (
                        <Chip key={label} label={label} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ))}
                      {(issue.labels || []).length > 3 && (
                        <Chip label={`+${issue.labels!.length - 3}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>{formatDate(issue.github_created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    {issue.jira_ticket_key ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Link href={issue.jira_ticket_url || '#'} target="_blank" rel="noopener">
                          <Chip label={issue.jira_ticket_key} size="small" color="primary" variant="outlined" clickable />
                        </Link>
                        <Tooltip title="Unlink JIRA ticket">
                          <Button
                            size="small"
                            variant="text"
                            sx={{ minWidth: 'auto', px: 0.5 }}
                            onClick={() => unlinkJiraMutation.mutate(issue.id)}
                            disabled={unlinkJiraMutation.isPending}
                          >
                            <LinkOffIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => createJiraMutation.mutate(issue.id)}
                          disabled={createJiraMutation.isPending}
                        >
                          {createJiraMutation.isPending ? 'Creating...' : 'Create'}
                        </Button>
                        <Tooltip title="Link existing JIRA ticket">
                          <Button
                            size="small"
                            variant="text"
                            sx={{ minWidth: 'auto', px: 0.5 }}
                            onClick={() => { setLinkJiraIssueId(issue.id); setLinkJiraKey(''); }}
                          >
                            <LinkIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {issue.jira_status && (
                      <Chip label={issue.jira_status} size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Link JIRA Dialog */}
      <Dialog open={!!linkJiraIssueId} onClose={() => { setLinkJiraIssueId(null); setLinkJiraKey(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Link JIRA Ticket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="JIRA Ticket Key"
            placeholder="e.g. PROJ-123"
            value={linkJiraKey}
            onChange={(e) => setLinkJiraKey(e.target.value.toUpperCase())}
            sx={{ mt: 1 }}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkJiraIssueId(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (linkJiraIssueId && linkJiraKey.trim()) {
                linkJiraMutation.mutate({ issueId: linkJiraIssueId, jiraKey: linkJiraKey.trim() });
              }
            }}
            disabled={!linkJiraKey.trim() || !/^[A-Z]+-\d+$/.test(linkJiraKey.trim()) || linkJiraMutation.isPending}
          >
            {linkJiraMutation.isPending ? 'Linking...' : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync Logs Dialog */}
      <Dialog open={syncLogsOpen} onClose={() => setSyncLogsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Sync History</DialogTitle>
        <DialogContent>
          {!syncLogs ? (
            <Skeleton variant="rectangular" height={200} />
          ) : syncLogs.length === 0 ? (
            <Typography color="text.secondary">No sync logs yet.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Fetched</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>JIRA</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.started_at)}</TableCell>
                      <TableCell><Chip label={log.sync_type} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={log.status === 'completed' ? 'success' : log.status === 'failed' ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>{log.issues_fetched}</TableCell>
                      <TableCell>{log.issues_created}</TableCell>
                      <TableCell>{log.issues_updated}</TableCell>
                      <TableCell>{log.jira_tickets_created}</TableCell>
                      <TableCell>
                        {log.error_message && (
                          <Tooltip title={log.error_message}>
                            <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 200 }}>
                              {log.error_message}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncLogsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
